import type { Backend } from "../api/backend";
import type { RadioLibraryTrack } from "../api/types";

/** MusicKit JS playback states (`MusicKit.PlaybackStates`). */
export const PlaybackState = {
  none: 0,
  loading: 1,
  playing: 2,
  paused: 3,
  stopped: 4,
  ended: 5,
  seeking: 6,
  waiting: 8,
  stalled: 9,
  completed: 10,
} as const;

const SCRIPT_URL = "https://js-cdn.music.apple.com/musickit/v3/musickit.js";

let scriptLoad: Promise<void> | null = null;

function loadScript(): Promise<void> {
  scriptLoad ??= new Promise<void>((resolve, reject) => {
    if (window.MusicKit) return resolve();
    document.addEventListener("musickitloaded", () => resolve(), { once: true });
    const script = document.createElement("script");
    script.src = SCRIPT_URL;
    script.async = true;
    script.onerror = () => {
      scriptLoad = null;
      reject(new Error("MusicKit JS did not load"));
    };
    document.head.appendChild(script);
  });
  return scriptLoad;
}

/**
 * Apple Music in the browser: MusicKit JS configured with the app's developer token
 * (from the backend) and authorized by the listener's Apple ID. The browser's own
 * version of the phone's MusicKit — the same catalog, the same subscription.
 */
export class AppleMusic {
  private constructor(
    readonly kit: MusicKit.MusicKitInstance,
    private readonly backend: Backend,
  ) {}

  static async load(backend: Backend): Promise<AppleMusic> {
    const [{ token }] = await Promise.all([backend.appleMusicDeveloperToken(), loadScript()]);
    const musicKit = window.MusicKit;
    if (!musicKit) throw new Error("MusicKit JS did not load");
    const kit = await musicKit.configure({
      developerToken: token,
      app: { name: "Radiola", build: "1.0" },
    });
    return new AppleMusic(kit, backend);
  }

  get isAuthorized(): boolean {
    return this.kit.isAuthorized;
  }

  /** Two-letter storefront the catalog IDs are valid in, e.g. `us`. */
  get storefront(): string {
    return (this.kit.storefrontCountryCode || "us").toLowerCase();
  }

  async authorize(): Promise<void> {
    await this.kit.authorize();
    await this.shareTokenWithServer();
  }

  async signOut(): Promise<void> {
    await this.kit.unauthorize();
  }

  /** The producer reads the listener's library on the server; it needs a live user
   * token for that. The phone keeps one there, so this only fills a gap. */
  private async shareTokenWithServer(): Promise<void> {
    try {
      const connection = await this.backend.appleMusicConnection();
      if (connection.enabled && (!connection.connected || connection.needs_refresh)) {
        await this.backend.storeAppleMusicUserToken(this.kit.musicUserToken);
      }
    } catch {
      // The station still starts: the start request carries a library sample.
    }
  }

  async songsByIDs(ids: string[]): Promise<MusicKit.Song[]> {
    const catalogIDs = ids.filter((id) => !id.startsWith("i."));
    if (catalogIDs.length === 0) return [];
    return this.fetchSongs(`v1/catalog/${this.storefront}/songs`, { ids: catalogIDs.join(",") });
  }

  async librarySong(id: string): Promise<MusicKit.Song | null> {
    const [song] = await this.fetchSongs(`v1/me/library/songs/${id}`);
    return song ?? null;
  }

  async songsByISRC(isrc: string): Promise<MusicKit.Song[]> {
    return this.fetchSongs(`v1/catalog/${this.storefront}/songs`, { "filter[isrc]": isrc });
  }

  async searchSongs(term: string): Promise<MusicKit.Song[]> {
    try {
      const response = (await this.kit.api.music(`v1/catalog/${this.storefront}/search`, {
        term,
        types: "songs",
        limit: "10",
      })) as unknown as { data: { results: { songs?: { data: MusicKit.Song[] } } } };
      return response.data.results.songs?.data ?? [];
    } catch {
      return [];
    }
  }

  /** Up to `limit` songs from the listener's library, in the start request's shape. */
  async librarySnapshot(limit = 150): Promise<RadioLibraryTrack[]> {
    const tracks: RadioLibraryTrack[] = [];
    for (let offset = 0; tracks.length < limit; offset += 100) {
      const page = await this.fetchSongs("v1/me/library/songs", {
        limit: "100",
        offset: String(offset),
        include: "catalog",
      });
      for (const song of page) tracks.push(libraryTrack(song));
      if (page.length < 100) break;
    }
    return tracks.slice(0, limit);
  }

  private async fetchSongs(path: string, query?: Record<string, string>): Promise<MusicKit.Song[]> {
    try {
      const response = await this.kit.api.music(path, query);
      return response.data.data ?? [];
    } catch {
      return [];
    }
  }
}

function libraryTrack(song: MusicKit.Song): RadioLibraryTrack {
  const catalog = (song as { relationships?: { catalog?: { data?: MusicKit.Song[] } } })
    .relationships?.catalog?.data?.[0];
  const attributes = catalog?.attributes ?? song.attributes;
  const year = Number.parseInt(attributes?.releaseDate?.slice(0, 4) ?? "", 10);
  return {
    apple_music_id: catalog?.id ?? song.attributes?.playParams?.catalogId ?? song.id,
    isrc: catalog?.attributes?.isrc ?? null,
    title: attributes?.name || "Untitled",
    artist: attributes?.artistName ?? "",
    album: attributes?.albumName ?? "",
    year: Number.isFinite(year) ? year : null,
    genres: (attributes?.genreNames ?? []).filter((genre) => genre !== "Music").slice(0, 8),
    duration: (attributes?.durationInMillis ?? 0) / 1000,
    artwork_url: attributes?.artwork ? artworkURL(attributes.artwork.url, 300) : null,
    has_play_parameters: Boolean(attributes?.playParams),
  };
}

export function artworkURL(template: string, side: number): string {
  return template.replace("{w}", String(side)).replace("{h}", String(side));
}
